import type { McpConnection } from '@ftthnms/shared';
import { Button } from '@ftthnms/ui';
import { useQuery } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { logout, useSession } from '../lib/session';

/**
 * Sign-out confirmation. Default: leave FTTH NMS only (Speedmax ID and other Speedmax apps stay
 * signed in). The checkbox adds Speedmax ID Single Logout.
 */
export function LogoutDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const { ability } = useSession();
  const [global, setGlobal] = useState(false);
  const [revokeAgents, setRevokeAgents] = useState(true);
  // AI agents keep working after a logout unless they are disconnected here (or in AI Agent).
  const agents = useQuery({
    queryKey: ['mcp-connections', false],
    queryFn: () => api<McpConnection[]>('/api/v1/mcp/connections'),
    enabled: open && ability.can('use', 'Mcp'),
  });
  const agentCount = agents.data?.length ?? 0;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await logout(global, revokeAgents);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <dialog ref={ref} className="fn-dialog" onClose={onClose} aria-labelledby="logout-title">
      <div
        className="fn-panel"
        style={{
          background: 'var(--bg-surface)',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="fn-tile fn-tile--danger">
            <LogOut className="i" aria-hidden />
          </span>
          <h2 id="logout-title" className="fn-card__title" style={{ fontSize: 15 }}>
            Keluar dari FTTH NMS?
          </h2>
        </div>
        <p className="fn-card__desc">
          Sesi Anda di FTTH NMS akan diakhiri. Aplikasi Speedmax lain yang sedang Anda buka tidak terpengaruh.
        </p>

        <label className="fn-choice" style={{ cursor: 'pointer' }}>
          <span>
            <span className="fn-choice__title">Keluar juga dari Speedmax ID</span>
            <span className="fn-choice__desc">
              Mengakhiri sesi di semua aplikasi Speedmax (Single Logout). Pakai ini di komputer bersama.
            </span>
          </span>
          <input
            id="logout-global"
            type="checkbox"
            checked={global}
            onChange={(e) => setGlobal(e.target.checked)}
            style={{ width: 18, height: 18, flex: 'none' }}
          />
        </label>

        {agentCount > 0 && (
          <label className="fn-choice" style={{ cursor: global ? 'default' : 'pointer' }}>
            <span>
              <span className="fn-choice__title">Putuskan juga {agentCount} AI agent yang tersambung</span>
              <span className="fn-choice__desc">
                {global
                  ? 'Keluar dari Speedmax ID selalu memutus semua AI agent.'
                  : 'Tanpa ini, AI agent (Claude, Cursor, …) tetap bisa memakai FTTH NMS atas nama Anda.'}
              </span>
            </span>
            <input
              id="logout-agents"
              type="checkbox"
              checked={global || revokeAgents}
              disabled={global}
              onChange={(e) => setRevokeAgents(e.target.checked)}
              style={{ width: 18, height: 18, flex: 'none' }}
            />
          </label>
        )}

        {error && (
          <div className="fn-alert fn-alert--danger" role="alert">
            <div className="fn-alert__body">{error}</div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Batal
          </Button>
          <Button
            variant="danger"
            onClick={() => void confirm()}
            loading={busy}
            icon={<LogOut className="i" aria-hidden />}
          >
            {global ? 'Keluar dari Semua Aplikasi' : 'Keluar'}
          </Button>
        </div>
      </div>
    </dialog>
  );
}
